import { Suspense, type ReactNode } from 'react'
import { Button } from '@floorfile/ui'
import { createBrowserRouter, Link, Navigate, RouterProvider, useLocation, useNavigate, useRouteError } from 'react-router-dom'
import { useCopy } from '@floorfile/i18n'
import { appCopy } from './copy'
import { AppShell } from './layout/AppShell'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { EnrollTwoFactorPage } from './pages/EnrollTwoFactorPage'
import { EmailVerificationGate, VerifyEmailPage } from './pages/EmailVerificationPages'
import { OfflineUnlockGate } from './pages/OfflineUnlockGate'
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages'
import { AcceptInvitePage } from './pages/AcceptInvitePage'
import { registerNav } from './nav-registry'
import { useAuth } from '@/lib/auth'
import { useModules } from '@/lib/modules'
import { isOfflineChunkError, lazyRoute } from '@/lib/lazy-route'
// Nav entries come from each feature's light `nav.ts` (icons + NavEntry only)
// so the eager bundle never touches feature pages/api (H-10/H-11).
import { workforceNav } from '@/features/workforce/nav'
import { formsNav } from '@/features/forms/nav'
import { documentsNav } from '@/features/documents/nav'
import { checksNav } from '@/features/checks/nav'
import { carsNav } from '@/features/cars/nav'
import { auditsNav } from '@/features/audits/nav'
import { assignmentNav } from '@/features/assignment/nav'
import { equipmentNav } from '@/features/equipment/nav'
import { telemetryRulesNav } from '@/features/equipment/telemetry-nav'
import { maintenanceNav } from '@/features/maintenance/nav'
import { downtimeNav } from '@/features/downtime/nav'
import { partsNav } from '@/features/parts/nav'
import { helpRequestsNav, incidentsNav } from '@/features/incidents/nav'
import { trainingNav } from '@/features/training/nav'
import { chatNav } from '@/features/chat/nav'
import { reportsNav } from '@/features/reports/nav'
import { aiNav } from '@/features/ai/nav'
import { settingsNav } from '@/features/settings/nav'
// Access rules only — a plain data module with no React or icon imports, so
// wiring the route guards does not drag the settings pages into the eager
// bundle (audit PRT-6).
import { canReachSettings, settingsAccessFor } from '@/features/settings/access'
import { settingsCopy } from '@/features/settings/copy'
import { ncrNav } from '@/features/ncr/nav'
import { changeControlNav } from '@/features/change-control/nav'
import { supplierQualityNav } from '@/features/supplier-quality/nav'
import { fmeaNav, riskRegisterNav } from '@/features/risk-fmea/nav'
import { deviationsNav, oosNav } from '@/features/deviations-oos/nav'
import { lotsNav } from '@/features/lots/nav'
import { esignNav } from '@/features/esign/nav'
import { hrRecordsNav } from '@/features/hr-records/nav'

// Route-level code splitting (H-10): every feature page is a lazy chunk,
// imported from its concrete page module — never the barrel, which would
// pull the whole feature back into one graph and defeat splitting.
// `lazyRoute` is React.lazy plus stale-deploy recovery: an autoUpdate service
// worker deletes the old precache out from under already-open tabs, which
// otherwise kills EVERY route below at once. See lib/lazy-route.ts.
// L-02: Notes is the Phase 1 reference module — not user-facing. The feature
// directory, API module, tables and tests all stay (it is the canonical
// module shape and the cheapest platform integration-test surface); it
// simply has no route and no nav entry.
// `AccountPage` is a logged-in settings route, not shell chrome, and was the
// one such page still imported eagerly — 21 kB in the entry chunk for a screen
// most sessions never open. The auth pages above it stay eager on purpose: they
// are what an unauthenticated visitor sees first, and splitting them would put
// a round-trip on the login path.
const AccountPage = lazyRoute(() => import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })))
const WorkersPage = lazyRoute(() => import('@/features/workforce/pages/WorkersPage').then((m) => ({ default: m.WorkersPage })))
const WorkerDetailPage = lazyRoute(() => import('@/features/workforce/pages/WorkerDetailPage').then((m) => ({ default: m.WorkerDetailPage })))
const LocationsPage = lazyRoute(() => import('@/features/workforce/pages/LocationsPage').then((m) => ({ default: m.LocationsPage })))
const OrgSettingsPage = lazyRoute(() => import('@/features/workforce/pages/OrgSettingsPage').then((m) => ({ default: m.OrgSettingsPage })))
const AttendancePage = lazyRoute(() => import('@/features/workforce/pages/AttendancePage').then((m) => ({ default: m.AttendancePage })))
const RosterPage = lazyRoute(() => import('@/features/workforce/pages/RosterPage').then((m) => ({ default: m.RosterPage })))
const FormLibraryPage = lazyRoute(() => import('@/features/forms/pages/FormLibraryPage').then((m) => ({ default: m.FormLibraryPage })))
const FormBuilderPage = lazyRoute(() => import('@/features/forms/pages/FormBuilderPage').then((m) => ({ default: m.FormBuilderPage })))
const FormDetailPage = lazyRoute(() => import('@/features/forms/pages/FormDetailPage').then((m) => ({ default: m.FormDetailPage })))
const DocumentLibraryPage = lazyRoute(() => import('@/features/documents/pages/DocumentLibraryPage').then((m) => ({ default: m.DocumentLibraryPage })))
const DocumentDetailPage = lazyRoute(() => import('@/features/documents/pages/DocumentDetailPage').then((m) => ({ default: m.DocumentDetailPage })))
const ChecksPage = lazyRoute(() => import('@/features/checks/pages/ChecksPage').then((m) => ({ default: m.ChecksPage })))
const CheckRunPage = lazyRoute(() => import('@/features/checks/pages/CheckRunPage').then((m) => ({ default: m.CheckRunPage })))
const CarsPage = lazyRoute(() => import('@/features/cars/pages/CarsPage').then((m) => ({ default: m.CarsPage })))
const CarDetailPage = lazyRoute(() => import('@/features/cars/pages/CarDetailPage').then((m) => ({ default: m.CarDetailPage })))
const AuditsPage = lazyRoute(() => import('@/features/audits/pages/AuditsPage').then((m) => ({ default: m.AuditsPage })))
const AuditDetailPage = lazyRoute(() => import('@/features/audits/pages/AuditDetailPage').then((m) => ({ default: m.AuditDetailPage })))
const AuditRunPage = lazyRoute(() => import('@/features/audits/pages/AuditRunPage').then((m) => ({ default: m.AuditRunPage })))
const InspectionsPage = lazyRoute(() => import('@/features/audits/pages/InspectionsPage').then((m) => ({ default: m.InspectionsPage })))
const AvailableWorkPage = lazyRoute(() => import('@/features/assignment/pages/AvailableWorkPage').then((m) => ({ default: m.AvailableWorkPage })))
const MyAssignmentsPage = lazyRoute(() => import('@/features/assignment/pages/MyAssignmentsPage').then((m) => ({ default: m.MyAssignmentsPage })))
const AssignmentConsolePage = lazyRoute(() => import('@/features/assignment/pages/AssignmentConsolePage').then((m) => ({ default: m.AssignmentConsolePage })))
const AutomationSettingsPage = lazyRoute(() => import('@/features/assignment/pages/AutomationSettingsPage').then((m) => ({ default: m.AutomationSettingsPage })))
const EquipmentListPage = lazyRoute(() => import('@/features/equipment/pages/EquipmentListPage').then((m) => ({ default: m.EquipmentListPage })))
const EquipmentDetailPage = lazyRoute(() => import('@/features/equipment/pages/EquipmentDetailPage').then((m) => ({ default: m.EquipmentDetailPage })))
const TelemetryRulesPage = lazyRoute(() => import('@/features/equipment/pages/TelemetryRulesPage').then((m) => ({ default: m.TelemetryRulesPage })))
const MaintenancePage = lazyRoute(() => import('@/features/maintenance/pages/MaintenancePage').then((m) => ({ default: m.MaintenancePage })))
const WorkOrderRunPage = lazyRoute(() => import('@/features/maintenance/pages/WorkOrderRunPage').then((m) => ({ default: m.WorkOrderRunPage })))
// Fiix audit Phase 3.1: the triage queue, and the PUBLIC per-facility request page (no shell, no account).
const WorkRequestsPage = lazyRoute(() => import('@/features/maintenance/pages/WorkRequestsPage').then((m) => ({ default: m.WorkRequestsPage })))
const PublicRequestPage = lazyRoute(() => import('@/features/maintenance/pages/PublicRequestPage').then((m) => ({ default: m.PublicRequestPage })))
const DowntimePage = lazyRoute(() => import('@/features/downtime/pages/DowntimePage').then((m) => ({ default: m.DowntimePage })))
const PartsPage = lazyRoute(() => import('@/features/parts/pages/PartsPage').then((m) => ({ default: m.PartsPage })))
const VendorsPage = lazyRoute(() => import('@/features/parts/pages/VendorsPage').then((m) => ({ default: m.VendorsPage })))
const PartDetailPage = lazyRoute(() => import('@/features/parts/pages/PartDetailPage').then((m) => ({ default: m.PartDetailPage })))
const VendorDetailPage = lazyRoute(() => import('@/features/parts/pages/VendorDetailPage').then((m) => ({ default: m.VendorDetailPage })))
const ReorderRequestPage = lazyRoute(() => import('@/features/parts/pages/ReorderRequestPage').then((m) => ({ default: m.ReorderRequestPage })))
const SafetyPage = lazyRoute(() => import('@/features/incidents/pages/SafetyPage').then((m) => ({ default: m.SafetyPage })))
const HelpRequestsPage = lazyRoute(() => import('@/features/incidents/pages/HelpRequestsPage').then((m) => ({ default: m.HelpRequestsPage })))
const IncidentDetailPage = lazyRoute(() => import('@/features/incidents/pages/IncidentDetailPage').then((m) => ({ default: m.IncidentDetailPage })))
const TrainingPage = lazyRoute(() => import('@/features/training/pages/TrainingPage').then((m) => ({ default: m.TrainingPage })))
const ChatPage = lazyRoute(() => import('@/features/chat/pages/ChatPage').then((m) => ({ default: m.ChatPage })))
const ChatMessagePage = lazyRoute(() => import('@/features/chat/pages/ChatMessagePage').then((m) => ({ default: m.ChatMessagePage })))
const InboxPage = lazyRoute(() => import('@/features/comments/pages/InboxPage').then((m) => ({ default: m.InboxPage })))
const NotificationSettingsPage = lazyRoute(() => import('@/features/notifications/pages/NotificationSettingsPage').then((m) => ({ default: m.NotificationSettingsPage })))
const DashboardPage = lazyRoute(() => import('@/features/reports/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const ReportsHubPage = lazyRoute(() => import('@/features/reports/pages/ReportsHubPage').then((m) => ({ default: m.ReportsHubPage })))
const ReportRunnerPage = lazyRoute(() => import('@/features/reports/pages/ReportRunnerPage').then((m) => ({ default: m.ReportRunnerPage })))
// Lazy on its own chunk: it pulls in react-grid-layout on top of recharts,
// which the 300 KB gzipped initial-bundle budget will not carry (plan step 11).
const DomainDashboardPage = lazyRoute(() => import('@/features/reports/pages/DomainDashboardPage').then((m) => ({ default: m.DomainDashboardPage })))
const AiHubPage = lazyRoute(() => import('@/features/ai/pages/AiHubPage').then((m) => ({ default: m.AiHubPage })))
const IntegrationsPage = lazyRoute(() => import('@/features/integrations/pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })))
const TelemetrySourcePage = lazyRoute(() => import('@/features/integrations/pages/TelemetrySourcePage').then((m) => ({ default: m.TelemetrySourcePage })))
const GovernancePage = lazyRoute(() => import('@/features/governance/pages/GovernancePage').then((m) => ({ default: m.GovernancePage })))
const ModulesSettingsPage = lazyRoute(() => import('@/features/modules/pages/ModulesSettingsPage').then((m) => ({ default: m.ModulesSettingsPage })))
const SettingsHubPage = lazyRoute(() => import('@/features/settings/pages/SettingsHubPage').then((m) => ({ default: m.SettingsHubPage })))
const CompanySettingsPage = lazyRoute(() => import('@/features/settings/pages/CompanySettingsPage').then((m) => ({ default: m.CompanySettingsPage })))
const PoliciesSettingsPage = lazyRoute(() => import('@/features/settings/pages/PoliciesSettingsPage').then((m) => ({ default: m.PoliciesSettingsPage })))
const NumberingSettingsPage = lazyRoute(() => import('@/features/settings/pages/NumberingSettingsPage').then((m) => ({ default: m.NumberingSettingsPage })))
const UsersSettingsPage = lazyRoute(() => import('@/features/settings/pages/UsersSettingsPage').then((m) => ({ default: m.UsersSettingsPage })))
const FacilitiesSettingsPage = lazyRoute(() => import('@/features/facilities/pages/FacilitiesSettingsPage').then((m) => ({ default: m.FacilitiesSettingsPage })))
const PartsSettingsPage = lazyRoute(() => import('@/features/parts/pages/PartsSettingsPage').then((m) => ({ default: m.PartsSettingsPage })))
const EquipmentMetricsSettingsPage = lazyRoute(() => import('@/features/equipment/pages/EquipmentMetricsSettingsPage').then((m) => ({ default: m.EquipmentMetricsSettingsPage })))
const WorkforceSettingsPage = lazyRoute(() => import('@/features/workforce/pages/WorkforceSettingsPage').then((m) => ({ default: m.WorkforceSettingsPage })))
const SystemHealthPage = lazyRoute(() => import('@/features/settings/pages/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })))
const FeaturesSettingsPage = lazyRoute(() => import('@/features/settings/pages/FeaturesSettingsPage').then((m) => ({ default: m.FeaturesSettingsPage })))
const AccessSettingsPage = lazyRoute(() => import('@/features/settings/pages/AccessSettingsPage').then((m) => ({ default: m.AccessSettingsPage })))
const TeamsPage = lazyRoute(() => import('@/features/workforce/pages/TeamPage').then((m) => ({ default: m.TeamsPage })))
const TeamPage = lazyRoute(() => import('@/features/workforce/pages/TeamPage').then((m) => ({ default: m.TeamPage })))
const WorkTransferPage = lazyRoute(() => import('@/features/work-transfer/pages/WorkTransferPage').then((m) => ({ default: m.WorkTransferPage })))
const AssignmentRulesSettingsPage = lazyRoute(() => import('@/features/settings/pages/AssignmentRulesSettingsPage').then((m) => ({ default: m.AssignmentRulesSettingsPage })))
const LocalizationSettingsPage = lazyRoute(() => import('@/features/settings/pages/LocalizationSettingsPage').then((m) => ({ default: m.LocalizationSettingsPage })))
const AuditLogPage = lazyRoute(() => import('@/features/settings/pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })))
/** 08-12 audit D4 item 3 — the export register moved off the Reports hub. It
 * lives in the reports FEATURE (it is that feature's data and hooks) but is
 * mounted under Settings, like `hr-access-log`. */
const NcrListPage = lazyRoute(() => import('@/features/ncr/pages/NcrListPage').then((m) => ({ default: m.NcrListPage })))
const NcrDetailPage = lazyRoute(() => import('@/features/ncr/pages/NcrDetailPage').then((m) => ({ default: m.NcrDetailPage })))
const ChangeRequestsPage = lazyRoute(() => import('@/features/change-control/pages/ChangeRequestsPage').then((m) => ({ default: m.ChangeRequestsPage })))
const ChangeRequestDetailPage = lazyRoute(() => import('@/features/change-control/pages/ChangeRequestDetailPage').then((m) => ({ default: m.ChangeRequestDetailPage })))
const ApprovalMatrixPage = lazyRoute(() => import('@/features/change-control/pages/ApprovalMatrixPage').then((m) => ({ default: m.ApprovalMatrixPage })))
const ChecksSettingsPage = lazyRoute(() => import('@/features/checks/pages/ChecksSettingsPage').then((m) => ({ default: m.ChecksSettingsPage })))
const HrSettingsPage = lazyRoute(() => import('@/features/hr-records/pages/HrSettingsPage').then((m) => ({ default: m.HrSettingsPage })))
const DeletedFormsPage = lazyRoute(() => import('@/features/forms/pages/DeletedFormsPage').then((m) => ({ default: m.DeletedFormsPage })))
const HrAccessLogPage = lazyRoute(() => import('@/features/hr-records/pages/HrAccessLogPage').then((m) => ({ default: m.HrAccessLogPage })))
const SupplierQualityPage = lazyRoute(() => import('@/features/supplier-quality/pages/SupplierQualityPage').then((m) => ({ default: m.SupplierQualityPage })))
const FmeaListPage = lazyRoute(() => import('@/features/risk-fmea/pages/FmeaListPage').then((m) => ({ default: m.FmeaListPage })))
const FmeaWorksheetPage = lazyRoute(() => import('@/features/risk-fmea/pages/FmeaWorksheetPage').then((m) => ({ default: m.FmeaWorksheetPage })))
const RiskRegisterPage = lazyRoute(() => import('@/features/risk-fmea/pages/RiskRegisterPage').then((m) => ({ default: m.RiskRegisterPage })))
const DeviationsPage = lazyRoute(() => import('@/features/deviations-oos/pages/DeviationsPage').then((m) => ({ default: m.DeviationsPage })))
const DeviationDetailPage = lazyRoute(() => import('@/features/deviations-oos/pages/DeviationDetailPage').then((m) => ({ default: m.DeviationDetailPage })))
const OosPage = lazyRoute(() => import('@/features/deviations-oos/pages/OosPage').then((m) => ({ default: m.OosPage })))
const OosDetailPage = lazyRoute(() => import('@/features/deviations-oos/pages/OosDetailPage').then((m) => ({ default: m.OosDetailPage })))
const LotRegisterPage = lazyRoute(() => import('@/features/lots/pages/LotRegisterPage').then((m) => ({ default: m.LotRegisterPage })))
const LotDetailPage = lazyRoute(() => import('@/features/lots/pages/LotDetailPage').then((m) => ({ default: m.LotDetailPage })))
const EsignAuditPage = lazyRoute(() => import('@/features/esign/pages/EsignAuditPage').then((m) => ({ default: m.EsignAuditPage })))
const HrRecordsPage = lazyRoute(() => import('@/features/hr-records/pages/HrRecordsPage').then((m) => ({ default: m.HrRecordsPage })))
const HrRecordDetailPage = lazyRoute(() => import('@/features/hr-records/pages/HrRecordDetailPage').then((m) => ({ default: m.HrRecordDetailPage })))
const ScanRedirectPage = lazyRoute(() => import('@/features/search/pages/ScanRedirectPage').then((m) => ({ default: m.ScanRedirectPage })))
const BulkLookupPage = lazyRoute(() => import('@/features/search/pages/BulkLookupPage').then((m) => ({ default: m.BulkLookupPage })))
const EntityHubPage = lazyRoute(() => import('@/features/search/pages/EntityHubPage').then((m) => ({ default: m.EntityHubPage })))
const SearchSettingsPage = lazyRoute(() => import('@/features/search/pages/SearchSettingsPage').then((m) => ({ default: m.SearchSettingsPage })))
// The admin plane loads as its own chunk — tenants never pay for it.
const AdminRoot = lazyRoute(() => import('@/features/admin/pages/AdminShell').then((m) => ({ default: m.AdminRoot })))
const RequireAdmin = lazyRoute(() => import('@/features/admin/pages/AdminShell').then((m) => ({ default: m.RequireAdmin })))
const AdminShell = lazyRoute(() => import('@/features/admin/pages/AdminShell').then((m) => ({ default: m.AdminShell })))
const AdminLoginPage = lazyRoute(() => import('@/features/admin/pages/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })))
const AdminCompaniesPage = lazyRoute(() => import('@/features/admin/pages/AdminCompaniesPage').then((m) => ({ default: m.AdminCompaniesPage })))
const AdminCompanyPage = lazyRoute(() => import('@/features/admin/pages/AdminCompanyPage').then((m) => ({ default: m.AdminCompanyPage })))
const AdminActionsPage = lazyRoute(() => import('@/features/admin/pages/AdminActionsPage').then((m) => ({ default: m.AdminActionsPage })))
const AdminHealthPage = lazyRoute(() => import('@/features/admin/pages/AdminHealthPage').then((m) => ({ default: m.AdminHealthPage })))
// Platform Console plan §12: plans & products, billing, the onboarding wizard.
const AdminCompanyWizardPage = lazyRoute(() => import('@/features/admin/pages/AdminCompanyWizardPage').then((m) => ({ default: m.AdminCompanyWizardPage })))
const AdminPlansPage = lazyRoute(() => import('@/features/admin/pages/AdminPlansPage').then((m) => ({ default: m.AdminPlansPage })))
const AdminPlanPage = lazyRoute(() => import('@/features/admin/pages/AdminPlanPage').then((m) => ({ default: m.AdminPlanPage })))
const AdminBillingPage = lazyRoute(() => import('@/features/admin/pages/AdminBillingPage').then((m) => ({ default: m.AdminBillingPage })))
const AdminReportsPage = lazyRoute(() => import('@/features/admin/pages/AdminReportsPage').then((m) => ({ default: m.AdminReportsPage })))

// Feature nav registrations — one line per feature (OVERVIEW §3).
// M-02: administrative surfaces (integrations, modules, search settings,
// governance, change-control settings, automation) have Settings-hub cards
// instead of sidebar entries — the sidebar is the operator's map. Their
// ROUTES remain below so bookmarks and deep links never 404. Notes (L-02)
// is the Phase 1 reference module, not a user feature. The Inbox (L-01)
// folded into the NotificationBell, which deep-links /inbox.
registerNav(chatNav, 'chat-comments')
registerNav(reportsNav, 'reports')
registerNav(aiNav, 'ai')
registerNav(checksNav, 'checks')
registerNav(carsNav, 'cars')
for (const entry of assignmentNav) {
  // Automation is a Settings-hub card, not a sidebar entry — the same skip
  // this array has always carried.
  if (entry.path === '/automation') continue
  registerNav(entry, 'assignment')
}
registerNav(equipmentNav, 'equipment')
registerNav(maintenanceNav, 'maintenance')
registerNav(downtimeNav, 'equipment')
// Telemetry rules ride the equipment module gate (UI plan Phase 8): the
// readings they watch are equipment's, so a company with Equipment switched off
// has nothing for a rule to see.
registerNav(telemetryRulesNav, 'equipment')
for (const entry of partsNav) registerNav(entry, entry.path === '/vendors' ? 'vendors' : 'parts')
registerNav(incidentsNav, 'incidents')
registerNav(helpRequestsNav, 'incidents')
registerNav(trainingNav, 'training')
for (const entry of auditsNav) registerNav(entry, 'audits')
for (const entry of workforceNav) registerNav(entry)
registerNav(formsNav, 'forms')
registerNav(documentsNav, 'documents')
registerNav(settingsNav)
registerNav(ncrNav, 'ncr')
registerNav(changeControlNav,'change_control')
registerNav(supplierQualityNav, 'supplier_quality')
registerNav(fmeaNav,'risk_fmea')
registerNav(riskRegisterNav,'risk_fmea')
registerNav(deviationsNav,'deviations_oos')
registerNav(oosNav,'deviations_oos')
registerNav(lotsNav,'lot_genealogy')
registerNav(esignNav,'regulated_esign')
registerNav(hrRecordsNav,'hr_records')

function RequireModule({ moduleKey, children }: { moduleKey: string; children: ReactNode }) {
  const copy = useCopy(appCopy).common
  const modules = useModules()
  if (modules.status === 'loading') return <p className="p-6 text-sm text-muted-foreground">{copy.loading}</p>
  return modules.isEnabled(moduleKey) ? <>{children}</> : <Navigate to="/" replace />
}

/**
 * Route guard for the settings area (audit PRT-6).
 *
 * PRT-6 was filed against `/settings/parts`, but NO `/settings/*` route had a
 * permission guard — only the API rejected the caller, so anyone typing the URL
 * reached the page and got an error-shaped screen instead of a clean denial,
 * leaking the existence and shape of admin configuration on the way.
 *
 * The rule comes from `SETTINGS_ACCESS`, the same table the settings hub filters
 * its cards with, so the card and the route cannot disagree. An undeclared path
 * is treated as the settings floor (`settings:read`) rather than as "allow" —
 * fail closed, and the guard test below makes the omission fail the build too.
 */
function RequireSettings({ path, children }: { path: string; children: ReactNode }) {
  const app = useCopy(appCopy)
  const copy = app.common
  const settingsWords = useCopy(settingsCopy)
  const { user, permissions } = useAuth()
  const modules = useModules()
  const access = settingsAccessFor(path) ?? {}
  if (access.moduleKey) {
    if (modules.status === 'loading') return <p className="p-6 text-sm text-muted-foreground">{copy.loading}</p>
    if (!modules.isEnabled(access.moduleKey)) return <Navigate to="/" replace />
  }
  // FF-AUDIT-064: the same refusal the shell shows for a gated area, not a
  // silent bounce to the dashboard that looked like a broken link.
  if (!canReachSettings(access, { role: user?.role, permissions })) {
    return (
      <section className="mx-auto max-w-xl rounded-lg border bg-card p-6">
        <h1 className="text-xl font-semibold">{app.gate.noAccessTitle}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{app.gate.noAccessArea}</p>
        <Link to="/" className="mt-4 inline-flex text-sm font-medium text-primary hover:underline">{app.shell.goToDashboard}</Link>
      </section>
    )
  }
  /**
   * FF-AUDIT-20260921-210633-009: the way back.
   *
   * None of the 22 settings sub-pages had a breadcrumb, a back link or a
   * settings rail — the only way out was the browser's Back button or the
   * sidebar, which lands on the hub again. Rendered HERE rather than in each
   * page because every settings route already passes through this guard, so
   * one edit covers all of them and a route added tomorrow gets it for free.
   *
   * The hub itself is excluded: a "← Settings" link on Settings is a link to
   * the page you are standing on.
   */
  return (
    <>
      {path !== '/settings' && (
        <Link to="/settings" className="mb-3 inline-flex text-sm text-muted-foreground hover:text-foreground hover:underline">
          {settingsWords.hub.backToSettings}
        </Link>
      )}
      {children}
    </>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const copy = useCopy(appCopy).common
  const {
    status,
    emailVerificationRequired,
    twoFactorSetupRequired,
    offlineEncryption,
    requireOfflineEncryption,
    signedOut,
  } = useAuth()
  const location = useLocation()
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        {copy.loading}
      </div>
    )
  }
    if (status === 'anonymous') {
      // Return-to-page is for a deep link opened while signed out. After an
      // explicit sign-out the page on screen was the PREVIOUS user's; the
      // next sign-in starts at the dashboard (owner report, 2026-09-27).
      return signedOut ? <Navigate to="/login" replace /> : <Navigate to="/login" replace state={{ from: location }} />
    }
    if (emailVerificationRequired) {
      return <EmailVerificationGate />
    }
  // Enrollment-only session (company-mandated 2FA): the API refuses every
  // other endpoint, so the app renders only the enrollment gate.
  if (twoFactorSetupRequired) {
    return <EnrollTwoFactorPage />
  }
  // Device offline-encryption gate (D24 §9): either this user has a PIN on
  // this device but hasn't unlocked it for this page session, or the
  // company forces offline encryption and this user hasn't enrolled here
  // yet. Opt-in by default — neither condition applies otherwise.
  if (offlineEncryption.enrolled ? !offlineEncryption.unlocked : requireOfflineEncryption) {
    return <OfflineUnlockGate />
  }
  return <>{children}</>
}

/**
 * Route-level error boundary (react-router errorElement): shown when a
 * page chunk fails to load (network hiccup / stale deploy) or a route
 * render throws. A reload fetches the freshly deployed chunks.
 *
 * Two cases since the precache became an allow-list (2026-08-03). A route
 * outside the eight offline-first screens is runtime-cached, so opening it for
 * the first time with no signal is a NORMAL outcome, not a fault — and
 * `Reload` is the wrong thing to offer, because reloading offline cannot fetch
 * what is not there. `lazyRoute` marks that case with `OfflineChunkError`
 * rather than leaving the boundary to guess from an engine-specific message.
 */
function RouteErrorCard() {
  const error = useRouteError()
  const c = useCopy(appCopy).routeError
  const offline = isOfflineChunkError(error)
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <section className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold">{offline ? c.offlineTitle : c.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{offline ? c.offlineBody : c.body}</p>
        {!offline && (
          <Button
            className="mt-4 inline-flex items-center justify-center"
            onClick={() => window.location.reload()}
          >
            {c.reload}
          </Button>
        )}
      </section>
    </div>
  )
}

/**
 * A URL that matches no route.
 *
 * Before this existed there was no catch-all, so an unmatched path fell to the
 * parent's `errorElement` and every bad address — a typo, a stale bookmark, a
 * link from an old email — rendered "Something went wrong loading this page"
 * with a `Reload` button. That is wrong twice: nothing went wrong, and
 * reloading a path no route declares does exactly nothing, so the one action
 * offered is the one action guaranteed not to help. It also hides real chunk
 * failures inside the same card, which is how a mistyped URL cost a session's
 * time being investigated as a routing bug.
 *
 * Rendered as the last CHILD of the authenticated shell rather than a top-level
 * route, so a signed-in user keeps the nav and can get somewhere in one click,
 * and a signed-out one is still sent to login by `RequireAuth` first.
 */
function NotFoundPage() {
  const c = useCopy(appCopy).notFound
  const navigate = useNavigate()
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <section className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold">{c.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {/* A real anchor, not a Button with an onClick: this is navigation,
              so it must middle-click, right-click and read as a link — and a
              static `to` is what `internal-links-resolve.test.ts` can see. */}
          <Link
            to="/"
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {c.home}
          </Link>
          <Button variant="outline" onClick={() => navigate(-1)}>
            {c.back}
          </Button>
        </div>
      </section>
    </div>
  )
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
    { path: '/register', element: <RegisterPage /> },
    { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // The invitation email's destination (FF-AUDIT-058) — public, token-bound.
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  // Fiix audit Phase 3.1: a facility's public work-request link / QR. The
  // token is the credential; the API answers 404 while the company's portal
  // setting is off. Outside RequireAuth on purpose — a stranger has no account.
  { path: '/request/:token', element: <PublicRequestPage /> },
  {
    // The platform-admin plane — its own provider/guard/chrome, no tenant nav
    // entry (SECURITY_&_UAC §5; Cloudflare Access fronts /admin in deployments).
    path: '/admin',
    element: <AdminRoot />,
    errorElement: <RouteErrorCard />,
    children: [
      { path: 'login', element: <AdminLoginPage /> },
      {
        element: (
          <RequireAdmin>
            <AdminShell />
          </RequireAdmin>
        ),
        children: [
          { index: true, element: <AdminCompaniesPage /> },
          { path: 'companies/new', element: <AdminCompanyWizardPage /> },
          { path: 'companies/:id', element: <AdminCompanyPage /> },
          { path: 'plans', element: <AdminPlansPage /> },
          { path: 'plans/:id', element: <AdminPlanPage /> },
          { path: 'billing', element: <AdminBillingPage /> },
          { path: 'reports', element: <AdminReportsPage /> },
          { path: 'health', element: <AdminHealthPage /> },
          { path: 'actions', element: <AdminActionsPage /> },
        ],
      },
    ],
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorCard />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 's/:code', element: <ScanRedirectPage /> },
      { path: 'search/bulk', element: <BulkLookupPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'reports', element: <ReportsHubPage /> },
      // BEFORE the `:definitionKey` catch-all — otherwise 'dashboards'
      // matches it and the runner 404s on a definition that never existed.
      { path: 'reports/dashboards/:dashboardKey', element: <DomainDashboardPage /> },
      { path: 'reports/:definitionKey', element: <ReportRunnerPage /> },
      { path: 'ai', element: <AiHubPage /> },
      { path: 'integrations', element: <IntegrationsPage /> },
      { path: 'integrations/telemetry-sources/:id', element: <TelemetrySourcePage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'chat/:channelId', element: <ChatPage /> },
      { path: 'chat/messages/:messageId', element: <ChatMessagePage /> },
      { path: 'inbox', element: <InboxPage /> },
      { path: 'settings/notifications', element: <RequireSettings path="/settings/notifications"><NotificationSettingsPage /></RequireSettings> },
      { path: 'settings/search', element: <RequireSettings path="/settings/search"><SearchSettingsPage /></RequireSettings> },
      { path: 'settings/governance', element: <RequireSettings path="/settings/governance"><GovernancePage /></RequireSettings> },
      { path: 'settings', element: <RequireSettings path="/settings"><SettingsHubPage /></RequireSettings> },
      { path: 'settings/company', element: <RequireSettings path="/settings/company"><CompanySettingsPage /></RequireSettings> },
      { path: 'settings/policies', element: <RequireSettings path="/settings/policies"><PoliciesSettingsPage /></RequireSettings> },
      { path: 'settings/numbering', element: <RequireSettings path="/settings/numbering"><NumberingSettingsPage /></RequireSettings> },
      { path: 'settings/users', element: <RequireSettings path="/settings/users"><UsersSettingsPage /></RequireSettings> },
      { path: 'settings/facilities', element: <RequireSettings path="/settings/facilities"><FacilitiesSettingsPage /></RequireSettings> },
      { path: 'settings/system-health', element: <RequireSettings path="/settings/system-health"><SystemHealthPage /></RequireSettings> },
      { path: 'settings/features', element: <RequireSettings path="/settings/features"><FeaturesSettingsPage /></RequireSettings> },
      { path: 'settings/access', element: <RequireSettings path="/settings/access"><AccessSettingsPage /></RequireSettings> },
      { path: 'settings/teams', element: <RequireSettings path="/settings/teams"><TeamsPage /></RequireSettings> },
      { path: 'settings/teams/:id', element: <RequireSettings path="/settings/teams"><TeamPage /></RequireSettings> },
      { path: 'settings/assignment-rules', element: <RequireSettings path="/settings/assignment-rules"><AssignmentRulesSettingsPage /></RequireSettings> },
      { path: 'settings/work-transfer', element: <RequireSettings path="/settings/work-transfer"><WorkTransferPage /></RequireSettings> },
      { path: 'settings/localization', element: <RequireSettings path="/settings/localization"><LocalizationSettingsPage /></RequireSettings> },
      { path: 'settings/audit-log', element: <RequireSettings path="/settings/audit-log"><AuditLogPage /></RequireSettings> },
      // Owner decision 2026-09-28: a person's own export files live on the Account
      // page (Members and Supervisors cannot open Settings); old links land there.
      { path: 'settings/exports', element: <Navigate to="/account#exports" replace /> },
      { path: 'settings/modules', element: <RequireSettings path="/settings/modules"><ModulesSettingsPage /></RequireSettings> },
      { path: 'settings/checks', element: <RequireSettings path="/settings/checks"><ChecksSettingsPage /></RequireSettings> },
      { path: 'settings/parts', element: <RequireSettings path="/settings/parts"><RequireModule moduleKey="parts"><PartsSettingsPage /></RequireModule></RequireSettings> },
      { path: 'settings/equipment-metrics', element: <RequireSettings path="/settings/equipment-metrics"><EquipmentMetricsSettingsPage /></RequireSettings> },
      { path: 'settings/workforce', element: <RequireSettings path="/settings/workforce"><WorkforceSettingsPage /></RequireSettings> },
      { path: 'settings/deleted-forms', element: <RequireSettings path="/settings/deleted-forms"><DeletedFormsPage /></RequireSettings> },
      { path: 'settings/hr-records', element: <RequireSettings path="/settings/hr-records"><RequireModule moduleKey="hr_records"><HrSettingsPage /></RequireModule></RequireSettings> },
      { path: 'settings/hr-access-log', element: <RequireSettings path="/settings/hr-access-log"><RequireModule moduleKey="hr_records"><HrAccessLogPage /></RequireModule></RequireSettings> },
      { path: 'checks', element: <ChecksPage /> },
      { path: 'checks/:id/run', element: <CheckRunPage /> },
      { path: 'cars', element: <CarsPage /> },
      { path: 'cars/:id', element: <CarDetailPage /> },
      { path: 'quality/nonconformances', element: <RequireModule moduleKey="ncr"><NcrListPage /></RequireModule> },
      { path: 'quality/nonconformances/:id', element: <RequireModule moduleKey="ncr"><NcrDetailPage /></RequireModule> },
      { path: 'quality/changes', element: <RequireModule moduleKey="change_control"><ChangeRequestsPage /></RequireModule> },
      { path: 'quality/changes/:id', element: <RequireModule moduleKey="change_control"><ChangeRequestDetailPage /></RequireModule> },
      { path: 'settings/change-control', element: <RequireSettings path="/settings/change-control"><RequireModule moduleKey="change_control"><ApprovalMatrixPage /></RequireModule></RequireSettings> },
      { path: 'suppliers/quality', element: <RequireModule moduleKey="supplier_quality"><SupplierQualityPage /></RequireModule> },
      { path: 'quality/fmea', element: <RequireModule moduleKey="risk_fmea"><FmeaListPage /></RequireModule> },
      { path: 'quality/fmea/:id', element: <RequireModule moduleKey="risk_fmea"><FmeaWorksheetPage /></RequireModule> },
      { path: 'quality/risks', element: <RequireModule moduleKey="risk_fmea"><RiskRegisterPage /></RequireModule> },
      { path: 'quality/deviations', element: <RequireModule moduleKey="deviations_oos"><DeviationsPage /></RequireModule> },
      { path: 'quality/deviations/:id', element: <RequireModule moduleKey="deviations_oos"><DeviationDetailPage /></RequireModule> },
      { path: 'quality/oos', element: <RequireModule moduleKey="deviations_oos"><OosPage /></RequireModule> },
      { path: 'quality/oos/:id', element: <RequireModule moduleKey="deviations_oos"><OosDetailPage /></RequireModule> },
      { path: 'quality/lots', element: <RequireModule moduleKey="lot_genealogy"><LotRegisterPage /></RequireModule> },
      { path: 'quality/lots/:id', element: <RequireModule moduleKey="lot_genealogy"><LotDetailPage /></RequireModule> },
      { path: 'quality/esign', element: <RequireModule moduleKey="regulated_esign"><EsignAuditPage /></RequireModule> },
      { path: 'people/hr-records', element: <RequireModule moduleKey="hr_records"><HrRecordsPage /></RequireModule> },
      { path: 'people/hr-records/:id', element: <RequireModule moduleKey="hr_records"><HrRecordDetailPage /></RequireModule> },
      // 08-12 audit E2: one console, three route contexts — the engine,
      // the records and the authorization are shared, so only the default
      // task-type filter differs.
      { path: 'checks/assignment', element: <AssignmentConsolePage taskType="CHECK" variant="checks" /> },
      { path: 'maintenance/dispatch', element: <AssignmentConsolePage taskType="WORK_ORDER" variant="maintenance" /> },
      { path: 'assignment/activity', element: <AssignmentConsolePage variant="activity" /> },
      { path: 'my-work', element: <AvailableWorkPage /> },
      { path: 'my-assignments', element: <MyAssignmentsPage /> },
      // 08-12 audit E2 item 5, done LAST and on purpose: the destinations
      // above had to exist before anything pointed at them. `/assignment`
      // keeps its route so bookmarks, user docs and old notification links
      // never 404 — the convention the registerNav comment above already
      // states, and this finding's own acceptance criterion ("existing deep
      // links do not become dead routes").
      { path: 'assignment', element: <Navigate to="/assignment/activity" replace /> },
      { path: 'automation', element: <AutomationSettingsPage /> },
      { path: 'equipment', element: <EquipmentListPage /> },
      { path: 'equipment/:id', element: <EquipmentDetailPage /> },
      { path: 'telemetry-rules', element: <TelemetryRulesPage /> },
      { path: 'maintenance', element: <MaintenancePage /> },
      { path: 'maintenance/requests', element: <WorkRequestsPage /> },
      { path: 'work-orders/:id/run', element: <WorkOrderRunPage /> },
      { path: 'downtime', element: <DowntimePage /> },
      { path: 'parts', element: <PartsPage /> },
      // F33: literal segment first — `parts/:id` would otherwise read "reorders" as an id.
      { path: 'parts/reorders/:id', element: <ReorderRequestPage /> },
      { path: 'parts/:id', element: <PartDetailPage /> },
      { path: 'parts/:id/reorder', element: <PartDetailPage openReorder /> },
      { path: 'vendors', element: <VendorsPage /> },
      // A real page rather than a bare EntityHubPage — the hub rendered no vendor
      // attributes at all, so phone/address were invisible (audit C-03).
      { path: 'vendors/:id', element: <VendorDetailPage /> },
      { path: 'safety', element: <SafetyPage /> },
      { path: 'help-requests', element: <HelpRequestsPage /> },
      { path: 'training', element: <TrainingPage /> },
      // Hub deep-link sub-views (AUDIT_FINDINGS M-05) — same page, URL-addressed tab.
      { path: 'training/matrix', element: <TrainingPage /> },
      { path: 'training/certifications', element: <TrainingPage /> },
      { path: 'safety/incidents/:id', element: <IncidentDetailPage /> },
      { path: 'audits', element: <AuditsPage /> },
      { path: 'audits/:id', element: <AuditDetailPage /> },
      { path: 'audits/:id/run', element: <AuditRunPage /> },
      { path: 'inspections', element: <InspectionsPage /> },
      { path: 'workers', element: <WorkersPage /> },
      { path: 'workers/:id', element: <WorkerDetailPage /> },
      { path: 'attendance', element: <AttendancePage /> },
      { path: 'roster', element: <RosterPage /> },
      { path: 'locations', element: <LocationsPage /> },
      { path: 'locations/:id', element: <EntityHubPage entityType="location" backTo="/locations" /> },
      { path: 'organization', element: <OrgSettingsPage /> },
      { path: 'forms', element: <FormLibraryPage /> },
      { path: 'forms/:id/edit', element: <FormBuilderPage /> },
      { path: 'forms/:id', element: <FormDetailPage /> },
      { path: 'documents', element: <DocumentLibraryPage /> },
      { path: 'documents/:id', element: <DocumentDetailPage /> },
      // LAST, and it must stay last: `*` matches anything, so any route
      // declared after it is unreachable.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function App() {
  const copy = useCopy(appCopy).common
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
          {copy.loading}
        </div>
      }
    >
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </Suspense>
  )
}
